import { basicAuth, fetchTokenAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const keywords =
  "hcm hris human capital management hr payroll workers erp financials";
const defaultUrlPattern = () => "https://[^/]*\\.(?:my)?workday\\.com/.*";
const publicEnvVars = ({ hostname, tenant }: Record<string, string>) => ({
  WORKDAY_URL: hostname ? `https://${hostname}` : "",
  WORKDAY_TENANT: tenant || "",
});

const workdayDetailsStep = {
  title: "Enter your Workday details",
  items: [
    {
      kind: "hostname" as const,
      label: "Hostname",
      required: true,
      placeholder: "wd2-impl-services1.workday.com",
      target: { kind: "configuration" as const, field: "hostname" },
      helperText:
        "Your Workday hostname, such as wd2-impl-services1.workday.com or wd5-services1.myworkday.com.",
    },
    {
      kind: "input" as const,
      label: "Tenant name",
      required: true,
      placeholder: "your-tenant",
      target: { kind: "configuration" as const, field: "tenant" },
      helperText:
        "Your Workday tenant name, found in tenant REST URLs after the service version (for example, https://tenant1.myworkday.com/api/common/v1/mycompany/… → mycompany).",
    },
  ],
} as const;

export const workday = defineProduct({
  name: "Workday",
  tagLine: "Enterprise HR and finance",
  logo,
  tags: ["it", "productivity"],
  variants: [
    productVariant({
      identifier: "workdayOAuth",
      authLabel: "REST OAuth",
      keywords,
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        fetchTokenAuth({
          request: {
            url: `https://${app.config.hostname}/ccx/oauth2/${app.config.tenant}/token`,
            method: "POST",
            formBody: {
              grant_type: "refresh_token",
              refresh_token: app.secret.refreshToken,
            },
            basicAuth: {
              username: app.config.clientId,
              password: app.secret.clientSecret,
            },
          },
          extract: { from: "jsonPath", paths: ["access_token"] },
          apply: { kind: "header", scheme: "Bearer" },
        }),
      steps: [
        workdayDetailsStep,
        {
          title: "Create OAuth credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Run **Register API Client for Integrations** in Workday, and select the functional-area scopes your workflows need. Then open **View API Clients**, select the client, choose **API Client → Manage Refresh Tokens for Integrations**, and generate a refresh token for the Workday account the workflows should act as.",
            },
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "configuration", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "secrets", field: "clientSecret" },
            },
            {
              kind: "input",
              label: "Refresh token",
              required: true,
              target: { kind: "secrets", field: "refreshToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ hostname, tenant }) => ({
          url: `https://${hostname}/api/common/v1/${tenant}/workers/me`,
          expectedStatus: [200, 403],
        }),
      },
      publicEnvVars,
    }),
    productVariant({
      identifier: "workday",
      authLabel: "SOAP or RaaS Basic auth",
      keywords,
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        basicAuth({
          username: app.secret.username,
          password: app.secret.password,
        }),
      steps: [
        workdayDetailsStep,
        {
          title: "Enter your Integration System User credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Use Basic auth only for Workday SOAP web services or RaaS report URLs. Workday REST APIs require OAuth 2.0; choose **REST OAuth** for `/api/{service}/{version}/{tenant}/…` URLs.",
            },
            {
              kind: "input",
              label: "Username",
              required: true,
              placeholder: "isu_3b@your-tenant",
              target: { kind: "secrets", field: "username" },
              helperText:
                "Typically in the form `username@tenant` (Workday includes the tenant in the username).",
            },
            {
              kind: "input",
              label: "Password",
              required: true,
              target: { kind: "secrets", field: "password" },
            },
            {
              kind: "test-url",
              label: "Test URL",
              placeholder:
                "https://wd2-impl-services1.workday.com/ccx/service/your-tenant/Human_Resources/v44.2",
              helperText:
                "Enter a SOAP web service or RaaS URL this account can access, or leave blank to skip testing.",
              urlTarget: { kind: "configuration", field: "testUrl" },
              methodTarget: { kind: "configuration", field: "testMethod" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ testUrl, testMethod }) => ({
          url: testUrl ?? "",
          method: testMethod === "POST" ? "POST" : "GET",
        }),
      },
      publicEnvVars,
    }),
  ],
});
